import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import PropTypes from 'prop-types';

import Forms from '../../2-atoms/forms/forms/forms';
import Checkbox from '../../2-atoms/forms/checkbox/checkbox';
import { IconArrowLink, IconClose } from '../../1-foundation/icons/icons';
import { getLabel } from '../../../../labels/labelling-service';
import Loader from '../../2-atoms/loader/loader';
import { useOutsideClick } from '../../../hooks/use-outside-click';
import Cta from '../../2-atoms/cta/cta';
import OverlayWrapper from '../overlay-wrapper/overlay-wrapper';

const DEFAULT_IS_ACTIVE = false;
const DEFAULT_IS_LOADING = false;

const ModalPrivacy = ({
  isActive = DEFAULT_IS_ACTIVE,
  isLoading = DEFAULT_IS_LOADING,
  htmlId,
  htmlProps,
  onCancel,
  onConfirm,
}) => {
  const FLAG_PRIVACY_READ_ID = `${htmlId}-privacy-read`;
  const FLAG_PRIVACY_CONSENT_ID = `${htmlId}-privacy-consent`;

  const [flags, setFlags] = useState({});
  const isConfirmed = flags[FLAG_PRIVACY_READ_ID] && flags[FLAG_PRIVACY_CONSENT_ID];

  const modalRef = useRef();

  // Reset flags every time the modal is closed
  useEffect(() => {
    if (!isActive) {
      setFlags({});
    }
  }, [isActive]);

  const handleCheckboxClick = (event) => setFlags({ ...flags, [event.target.id]: event.target.checked });

  useOutsideClick(modalRef, onCancel);

  return (
    <OverlayWrapper showOverlay={isActive}>
      <div className="c-modal c-modal--privacy" data-state="active">
        <div className="c-modal__container-ext" ref={modalRef}>
          {isLoading && <Loader modifiers={['medium']} withWrapper />}
          <div className="c-modal__container-int">
            <div className="c-modal__toolbar">
              <button type="button" className="c-modal__tool c-modal__tool--close" onClick={onCancel}>
                <IconClose modifiers={['navigation', 'c-medium-light', 'xsmall', 'close']} />
              </button>
            </div>
            <div className="c-modal__content-wrapper">
              <div className="c-modal__header">
                <h4 className="c-modal__content-title">{getLabel('modal.privacy.title')}</h4>
              </div>
              <div className="c-modal__content">
                <p
                  className="c-modal__content-description"
                  dangerouslySetInnerHTML={{ __html: htmlProps.consentText }}
                />
                <div className="c-modal__content-flags">
                  <Forms>
                    <Checkbox
                      id={FLAG_PRIVACY_READ_ID}
                      onClick={handleCheckboxClick}
                      isChecked={flags[FLAG_PRIVACY_READ_ID]}
                    >
                      <div dangerouslySetInnerHTML={{ __html: htmlProps.consentLabel1 }} />
                    </Checkbox>
                    <Checkbox
                      id={FLAG_PRIVACY_CONSENT_ID}
                      onClick={handleCheckboxClick}
                      isChecked={flags[FLAG_PRIVACY_CONSENT_ID]}
                    >
                      <div dangerouslySetInnerHTML={{ __html: htmlProps.consentLabel2 }} />
                    </Checkbox>
                    <p
                      className="c-modal__mandatory-text"
                      dangerouslySetInnerHTML={{ __html: htmlProps.consentMandatoryText }}
                    />
                  </Forms>
                </div>
              </div>
              <div className="c-modal__footer">
                <Cta modifiers={['no-outline', 'capitalized']} onClick={onCancel}>
                  {getLabel('global.cancel')}
                </Cta>
                <Cta
                  modifiers={['no-outline']}
                  disabled={!isConfirmed}
                  onClick={onConfirm}
                  append={<IconArrowLink modifiers={['arrow-navigation', 'arrow-link', 'c-primary']} />}
                >
                  {getLabel('global.confirm')}
                </Cta>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OverlayWrapper>
  );
};

ModalPrivacy.propTypes = {
  isActive: PropTypes.bool,
  isLoading: PropTypes.bool,
  htmlId: PropTypes.string.isRequired,
  htmlProps: PropTypes.shape({
    consentText: PropTypes.string.isRequired,
    consentLabel1: PropTypes.string.isRequired,
    consentLabel2: PropTypes.string.isRequired,
    consentMandatoryText: PropTypes.string.isRequired,
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ModalPrivacy;
